import React from 'react';
import PropTypes from 'prop-types';

import SeatMap from './seat-map';

import { minimapScroll, width, get_coords } from '../minimap/minimap.js';
import '../css/minimap.css';

const get_minimap_parent = (forMap) =>
    document.querySelector(`.throne-minimap.for-${forMap}`);

function MinimapSeat() {
    return <div className="minimap-seat"></div>;
}

function MinimapScroller({ forMap }) {
    const [moving, setMoving] = React.useState(false);
    const [lastCoords, setLastCoords] = React.useState(null);

    const scroller = React.useRef(null);
    const seatmap_table = document.querySelector(`#seatmap-${forMap}`);

    const onMouseDown = React.useCallback(
        ({ nativeEvent }) => {
            setMoving(true);
            const { x, y } = get_coords(nativeEvent);
            setLastCoords({ x, y });
        },
        [setMoving, setLastCoords],
    );

    const onMouseUp = React.useCallback(() => {
        setMoving(false);
    }, [setMoving]);

    const resizeMiniMapViewer = () => {
        const _seatmap_table = document.querySelector(`#seatmap-${forMap}`);
        if (_seatmap_table && scroller.current) {
            const tbody = _seatmap_table.querySelector('tbody');
            const viewportRatio = width(tbody) / width(_seatmap_table);
            const parentContainer = get_minimap_parent(forMap);
            const scroller_width = width(parentContainer) / viewportRatio;

            // We are using this type of style modification
            // instead of style prop for performance reasons
            // this way we do not require a react re-render on style change
            scroller.current.style.width = `${scroller_width}px`;
        }
    };

    // when we resize the window we resize the minimap square preview
    React.useEffect(() => {
        window.addEventListener('resize', resizeMiniMapViewer);
    }, []);

    // on first render we call this function to resize the minimap preview square
    React.useEffect(() => {
        resizeMiniMapViewer();
    }, []);

    const onMouseMove = React.useCallback(
        ({ nativeEvent }) => {
            let leftScroll = minimapScroll({
                element: scroller.current,
                event: nativeEvent,
                forMap,
                moving,
                lastCoords,
                setLastCoords,
            });

            if (!leftScroll) {
                return;
            }
            // we get the current minimap scroll position
            const tableMinimapLeftScroll = document.querySelector(
                `.throne-minimap.for-${forMap} .seatmap-container`,
            )
                ? document.querySelector(
                    `.throne-minimap.for-${forMap} .seatmap-container`,
                ).scrollLeft
                : 0;
            // we get the total minimap width and not only visible width
            const parentContainer = document.querySelector(
                `.throne-minimap.for-${forMap} tbody`,
            );
            const seatmapContainer = seatmap_table.querySelector('tbody');

            const minimapRatio = width(seatmapContainer) / width(parentContainer);

            let scrollable = seatmap_table.querySelector('table');
            // we add on the map leftScroll the minimap leftScroll
            scrollable.scrollTo(
                (leftScroll + tableMinimapLeftScroll) * minimapRatio,
                0,
            );
        },
        [forMap, lastCoords, moving, seatmap_table],
    );

    return (
        <div
            ref={scroller}
            className="minimap-scroller-container"
            onMouseUp={onMouseUp}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onTouchStart={onMouseDown}
            onTouchEnd={onMouseUp}
            onTouchMove={onMouseMove}
        >
            <div className="minimap-scroller"></div>
        </div>
    );
}

MinimapScroller.propTypes = {
    forMap: PropTypes.string.isRequired,
};

export default function Minimap({ forMap, zone, ...props }) {
    return (
        <div className={`throne-minimap for-${forMap}`}>
            <SeatMap
                isMinimap={true}
                zone={zone}
                zoomControls={false}
                draggable={false}
                SeatElement={MinimapSeat}
                defaultZoomLevel={0.1}
                {...props}
            />
            <MinimapScroller forMap={forMap} />
        </div>
    );
}

Minimap.propTypes = {
    forMap: PropTypes.string.isRequired,
    zone: PropTypes.object.isRequired,
};
